import { OrganizationCountry } from "../enums/country";
export const bankAccountNumberPlaceholder = (country) => {
    if (country === OrganizationCountry.Australia) {
        return "000123456";
    }
    if (country === OrganizationCountry.GreatBritain) {
        return "12345678";
    }
    if (country === OrganizationCountry.Ireland) {
        return "IE29AIBK93115212345678";
    }
    if (country === OrganizationCountry.Germany) {
        return "DE89370400440532013000";
    }
    return "000123456789";
};
export const bankAccountNumberAutoComplete = (country) => {
    return country === OrganizationCountry.UnitedStates ? "off" : undefined;
};
export const bankAccountNumberInputMode = (country) => {
    return country === OrganizationCountry.UnitedStates || country === OrganizationCountry.Australia
        ? "numeric"
        : undefined;
};
export const sanitizeDigitsOnly = (value, options = { preserveLeadingZero: true }) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    if (options.preserveLeadingZero) {
        return digitsOnly;
    }
    return digitsOnly.replace(/^0+/, "") || "0";
};
export const sanitizeBankAccountNumber = (country, value) => {
    if (country === OrganizationCountry.UnitedStates) {
        return sanitizeDigitsOnly(value, { preserveLeadingZero: true });
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return value.replace(/\s+/g, "").toUpperCase();
    }
    return value;
};
export const isIeIbanValid = (iban) => {
    return /^IE\d{2}[A-Z]{4}\d{14}$/.test(iban.replace(/\s+/g, "").toUpperCase());
};
export const isDeIbanValid = (iban) => {
    return /^DE\d{2}\d{8}\d{10}$/.test(iban.replace(/\s+/g, "").toUpperCase());
};
const hasOnlyNumbersRule = {
    name: "hasOnlyNumbers",
    messageKey: "createBankAccount.accountNumberOnlyDigits",
    isValid: (value) => /^\d+$/.test(value),
};
const usAccountNumberLengthRule = {
    name: "isAccountNumberLengthValidUs",
    messageKey: "createBankAccount.accountNumberInvalidLengthUs",
    isValid: (value) => value.length >= 4 && value.length <= 17,
};
const auAccountNumberLengthRule = {
    name: "isAccountNumberLengthValidAu",
    messageKey: "createBankAccount.accountNumberInvalidLengthAu",
    isValid: (value) => value.length >= 6 && value.length <= 10,
};
const ieIbanRule = {
    name: "isIbanValidIe",
    messageKey: "createBankAccount.accountNumberInvalidIbanIe",
    isValid: isIeIbanValid,
};
const deIbanRule = {
    name: "isIbanValidDe",
    messageKey: "createBankAccount.accountNumberInvalidIbanDe",
    isValid: isDeIbanValid,
};
export const getBankAccountNumberRules = (country) => {
    if (country === OrganizationCountry.UnitedStates) {
        return [hasOnlyNumbersRule, usAccountNumberLengthRule];
    }
    if (country === OrganizationCountry.Australia) {
        return [hasOnlyNumbersRule, auAccountNumberLengthRule];
    }
    if (country === OrganizationCountry.Ireland) {
        return [ieIbanRule];
    }
    if (country === OrganizationCountry.Germany) {
        return [deIbanRule];
    }
    return [hasOnlyNumbersRule];
};
export const getAccountNumberInvalidErrorKey = (country) => {
    const countrySpecificRule = country
        ? getBankAccountNumberRules(country).find(({ name }) => name !== "hasOnlyNumbers")
        : undefined;
    return countrySpecificRule?.messageKey ?? "bankAccountStep.error.accountNumberInvalid";
};
export const getOnboardingBankSteps = (country) => {
    if (!country) {
        return [];
    }
    if (country === OrganizationCountry.Canada) {
        return [
            "bank.onboardingStepper.step.bank-account.transit",
            "bank.onboardingStepper.step.bank-account.institution",
            "bank.onboardingStepper.step.bank-account.account",
        ];
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return ["bank.onboardingStepper.step.bank-account.account"];
    }
    return ["bank.onboardingStepper.step.bank-account.account", "bank.onboardingStepper.step.bank-account.routing"];
};
// Helper to get the full routing number based on country
export const getFullRoutingNumber = (country, data) => {
    if (country === OrganizationCountry.Canada) {
        return `${data.transitNumber}${data.institutionNumber}`;
    }
    if (country === OrganizationCountry.GreatBritain) {
        return data.sortCode;
    }
    if (country === OrganizationCountry.Australia) {
        return data.bsb;
    }
    // SEPA/IBAN country: Stripe derives the bank from the IBAN, no routing number exists
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return "";
    }
    return data.routingNumber;
};
export const getBankAccountDefaultValues = (country, externalAccount) => {
    if (country === OrganizationCountry.Canada) {
        return {
            transitNumber: externalAccount?.routingNumber?.split("-")?.[0],
            institutionNumber: externalAccount?.routingNumber?.split("-")?.[1],
        };
    }
    if (country === OrganizationCountry.GreatBritain) {
        return {
            sortCode: externalAccount?.routingNumber
                ? sanitizeDigitsOnly(externalAccount.routingNumber)
                : externalAccount?.routingNumber,
        };
    }
    if (country === OrganizationCountry.Australia) {
        return { bsb: externalAccount?.routingNumber };
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return {};
    }
    return { routingNumber: externalAccount?.routingNumber };
};
export const getBankAccountFields = (country) => {
    if (country === OrganizationCountry.GreatBritain) {
        return "sortCode";
    }
    if (country === OrganizationCountry.Canada) {
        return "transitAndInstitution";
    }
    if (country === OrganizationCountry.UnitedStates) {
        return "routing";
    }
    if (country === OrganizationCountry.Australia) {
        return "bsb";
    }
    if (country === OrganizationCountry.Ireland || country === OrganizationCountry.Germany) {
        return "iban";
    }
    return null;
};
export const isStripePendingVerificationForCountry = (country) => {
    return country === OrganizationCountry.Canada;
};
export const hasBankDetectionFlow = (country) => {
    return country === OrganizationCountry.UnitedStates;
};
export const shouldDisplayBankLinkedSuccessParam = (country) => {
    return country === OrganizationCountry.UnitedStates;
};
